import React from "react";
import { FaFileExport } from "react-icons/fa6";
import * as XLSX from "xlsx";

const VendorDetailedTable = ({
    vendorData,
    summary,
    aggregated,
    company,
    selectedOutlet,
    startDate,
    endDate,
    formatSalesValue,
}) => {
    const hasData =
        vendorData &&
        vendorData.length > 0;

    const exportToExcel = () => {
        if (!vendorData.length) return;

        const rows = [];

        const totalColumns = aggregated ? 8 : 9;

        /* =======================
           HEADER SECTION
        ========================*/

        rows.push([company]);
        rows.push([selectedOutlet || ""]);
        rows.push([
            `Detailed Purchase Report From: ${startDate.toLocaleDateString()} To: ${endDate.toLocaleDateString()}`
        ]);
        rows.push([]); // empty row

        /* =======================
           TABLE HEADER
        ========================*/

        const headerRow = [
            "Vendor Name",
            "Item Name",
            "Brand Name",
            "Department",
            "UOM",
            "Units Ordered",
            "Rate",
            "Total",
        ];

        if (!aggregated) {
            headerRow.push("Purchase Bill Number");
        }

        rows.push(headerRow);

        /* =======================
           DATA SECTION
        ========================*/

        vendorData.forEach((vendor) => {

            // Vendor Title
            rows.push([vendor.vendor]);

            const purchaseItems = vendor.items.filter(i => i.source_type === "purchase");
            const debitItems = vendor.items.filter(i => i.source_type === "debitnote");

            const purchaseTotal = purchaseItems.reduce((sum, i) => sum + i.Total, 0);
            const debitTotal = debitItems.reduce((sum, i) => sum + i.Total, 0);
            const netTotal = purchaseTotal - debitTotal;

            /* ========= PURCHASE ROWS ========= */
            purchaseItems.forEach((item) => {
                const row = [
                    "",
                    item.Name,
                    item.BrandName || "-",
                    item.Department,
                    item.UOM,
                    item.ItemCount,
                    item.ItemRate,
                    item.Total,
                ];

                if (!aggregated) {
                    row.push(Number(item.purchaseBillNumber) || "-");
                }

                rows.push(row);
            });

            /* ========= PURCHASE TOTAL ========= */
            let purchaseRow = new Array(totalColumns).fill("");
            purchaseRow[6] = "Purchase Total";
            purchaseRow[7] = purchaseTotal;
            rows.push(purchaseRow);

            /* ========= DEBIT NOTE ROWS ========= */
            if (debitItems.length > 0) {

                debitItems.forEach((item) => {
                    const row = [
                        "",
                        item.Name,
                        item.BrandName || "-",
                        item.Department,
                        item.UOM,
                        -item.ItemCount,   // NEGATIVE
                        item.ItemRate,
                        -item.Total,       // NEGATIVE
                    ];

                    if (!aggregated) {
                        row.push(Number(item.purchaseBillNumber) || "-");
                    }

                    rows.push(row);
                });

                /* ========= DEBIT TOTAL ========= */
                let debitRow = new Array(totalColumns).fill("");
                debitRow[6] = "Debit Total";
                debitRow[7] = -debitTotal;
                rows.push(debitRow);

                /* ========= NET TOTAL ========= */
                let netRow = new Array(totalColumns).fill("");
                netRow[6] = "Net Total";
                netRow[7] = netTotal;
                rows.push(netRow);
            }

            // spacing between vendors
            rows.push([]);
        });

        /* =======================
           GRAND TOTAL
        ========================*/

        const computedDepartmentTotals = vendorData.reduce((acc, vendor) => {
            vendor.items.forEach(item => {
                const dept = item.Department?.trim() || "Unknown";

                if (!acc[dept]) {
                    acc[dept] = 0;
                }

                if (item.source_type === "purchase") {
                    acc[dept] += parseFloat(item.Total);
                } else if (item.source_type === "debitnote") {
                    acc[dept] -= parseFloat(item.Total);
                }
            });

            return acc;
        }, {});

        rows.push([]);
        rows.push(["Purchase Summary"]);

        Object.entries(computedDepartmentTotals).forEach(([deptName, total]) => {
            rows.push([`${deptName} Total`, total]);
        });

        rows.push([
            "Overall Total",
            Object.values(computedDepartmentTotals).reduce((a, b) => a + b, 0)
        ]);

        /* =======================
           CREATE WORKSHEET
        ========================*/

        const worksheet = XLSX.utils.aoa_to_sheet(rows);

        // Auto column width
        worksheet["!cols"] = headerRow.map((_, i) => ({
            wch: Math.max(
                10,
                ...rows.map(row =>
                    row[i] ? row[i].toString().length : 10
                )
            )
        }));

        // Merge title rows across all columns
        worksheet["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
            { s: { r: 2, c: 0 }, e: { r: 2, c: totalColumns - 1 } },
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase Report");


        const formatDateForFile = (date) => {
            return new Date(date)
                .toISOString()
                .split("T")[0];
        };

        const sanitize = (text) => {
            return text
                .replace(/[\/\\:*?"<>|]/g, "")
                .replace(/\s+/g, "_")
                .trim();
        };

        const outletName = sanitize(selectedOutlet || "Outlet");
        const formattedStart = formatDateForFile(startDate);
        const formattedEnd = formatDateForFile(endDate);

        const fileName =
            `Vendor_Wise_Purchase_Report_${outletName}_${formattedStart}_to_${formattedEnd}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    const computedDepartmentTotals = vendorData.reduce((acc, vendor) => {
        vendor.items.forEach(item => {
            const dept = item.Department?.trim() || "Unknown";

            if (!acc[dept]) {
                acc[dept] = 0;
            }

            if (item.source_type === "purchase") {
                acc[dept] += parseFloat(item.Total);
            } else if (item.source_type === "debitnote") {
                acc[dept] -= parseFloat(item.Total);
            }
        });

        return acc;
    }, {});

    if (!hasData) {
        return (
            <div className="max-w-5xl mx-auto mt-4 text-sm italic font-semibold text-center rounded-md bg-gray-50 p-4">
                No purchase data available. Select dates and click on View.
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-5xl">
            <div className="flex justify-between items-start mb-5 px-10">
                <div className="flex-1 flex justify-start" />

                <div className="flex-1 flex justify-center">
                    {computedDepartmentTotals && (
                        <div className="p-3 w-[400px] rounded-xl border border-gray-200 shadow-sm bg-gray-50 text-sm">
                            <div className="grid grid-cols-2 gap-y-3">
                                {Object.entries(computedDepartmentTotals).map(([deptName, total], index) => (
                                    <React.Fragment key={index}>
                                        <div className="text-gray-600">
                                            {deptName} Total
                                        </div>
                                        <div className="text-right font-medium">
                                            {formatSalesValue(total)}
                                        </div>
                                    </React.Fragment>
                                ))}

                                <div className="font-semibold text-base border-t pt-2">
                                    Overall Total
                                </div>
                                <div className="text-right font-bold text-base border-t pt-2">
                                    {formatSalesValue(
                                        Object.values(computedDepartmentTotals).reduce((a, b) => a + b, 0)
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-1 flex justify-end">
                    <button
                        onClick={exportToExcel}
                        className="flex items-center gap-2 px-3 py-2 text-sm rounded-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md"
                    >
                        <FaFileExport className="h-3 w-3" />
                        Export
                    </button>
                </div>
            </div>

            <h1 className="text-3xl text-center underline font-bold">
                {company}
            </h1>

            <h1 className="text-lg md:text-xl text-center font-medium">
                {selectedOutlet}
            </h1>

            <p className="text-center underline font-semibold">
                Detailed Purchase Report From: {startDate.toLocaleDateString()} To: {endDate.toLocaleDateString()}
            </p>

            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="text-center font-bold underline text-sm">
                        <th className="p-2">Vendor Name</th>
                        <th className="p-2">Item Name</th>
                        <th className="p-2">Brand Name</th>
                        <th className="p-2">UOM</th>
                        <th className="p-2">Units Ordered</th>
                        <th className="p-2">Rate</th>
                        <th className="p-2">Total</th>
                        {!aggregated && (
                            <th className="p-2">Purchase Bill Number</th>
                        )}
                    </tr>
                </thead>

                <tbody>
                    {vendorData.map((vendor, vendorIndex) => {
                        const purchaseItems = vendor.items.filter(item => item.source_type === "purchase");
                        const debitNoteItems = vendor.items.filter(item => item.source_type === "debitnote");

                        const purchaseTotal = purchaseItems.reduce((sum, item) => sum + item.Total, 0);
                        const debitTotal = debitNoteItems.reduce((sum, item) => sum + item.Total, 0);
                        const netTotal = purchaseTotal - debitTotal;

                        return (
                            <React.Fragment key={vendorIndex}>

                                {/* Vendor Name */}
                                <tr>
                                    <td className="font-bold capitalize p-1 border-b-2 border-black text-base text-center">
                                        {vendor.vendor}
                                    </td>
                                </tr>

                                {/* ================= PURCHASE ITEMS ================= */}
                                {purchaseItems.map((item, itemIndex) => (
                                    <tr key={`p-${itemIndex}`} className="text-center text-sm">
                                        <td></td>
                                        <td className="px-2 py-1">{item.Name}</td>
                                        <td className="px-2 py-1">{item.BrandName || "-"}</td>
                                        <td className="px-2 py-1">{item.UOM}</td>
                                        <td className="px-2 py-1">{item.ItemCount}</td>
                                        <td className="px-2 py-1">{formatSalesValue(item.ItemRate)}</td>
                                        <td className="px-2 py-1">{formatSalesValue(item.Total)}</td>
                                        {!aggregated && (
                                            <td className="px-2 py-1">{item.purchaseBillNumber || "-"}</td>
                                        )}
                                    </tr>
                                ))}

                                {/* Purchase Total */}
                                <tr>
                                    <td colSpan="6"></td>
                                    <td className="font-bold text-center border-t-2 border-gray-600 pb-1">
                                        {formatSalesValue(purchaseTotal)}
                                    </td>
                                    {!aggregated && <td></td>}
                                </tr>

                                {/* ================= DEBIT NOTE SECTION ================= */}
                                {debitNoteItems.length > 0 && (
                                    <>
                                        {debitNoteItems.map((item, itemIndex) => (
                                            <tr key={`d-${itemIndex}`} className="text-center text-sm text-red-600 font-medium">
                                                <td></td>
                                                <td className="px-2 py-1">{item.Name}</td>
                                                <td className="px-2 py-1">{item.BrandName || "-"}</td>
                                                <td className="px-2 py-1">{item.UOM}</td>
                                                <td className="px-2 py-1">-{item.ItemCount}</td>
                                                <td className="px-2 py-1">{formatSalesValue(item.ItemRate)}</td>
                                                <td className="px-2 py-1">-{formatSalesValue(item.Total)}</td>
                                                {!aggregated && (
                                                    <td className="px-2 py-1">{item.purchaseBillNumber || "-"}</td>
                                                )}
                                            </tr>
                                        ))}

                                        {/* Debit Total */}
                                        <tr>
                                            <td colSpan="6"></td>
                                            <td className="font-bold text-center border-t-2 border-red-400 text-red-600 pb-2">
                                                -{formatSalesValue(debitTotal)}
                                            </td>
                                            {!aggregated && <td></td>}
                                        </tr>
                                    </>
                                )}

                                {/* ========= NET TOTAL ========= */}
                                {debitNoteItems.length > 0 && (
                                    <tr>
                                        <td colSpan="6"></td>
                                        <td className="font-bold text-center border-t-2 border-black text-green-700">
                                            {formatSalesValue(netTotal)}
                                        </td>
                                        {!aggregated && <td></td>}
                                    </tr>
                                )}

                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default VendorDetailedTable;
